import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";
import { useEffect, useRef, useState } from "react";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import ResidentialEstimateNote from "../ResidentialEstimateNote";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";
import { sortByItemCode } from "../../../../../utils/Common/sortItemCode";

interface Props {
  prefill?: ProgressReportPrefill;
  onPhaseNotesChange?: (notes: Record<string, string>) => void;
  scheduleNotes?: string;
  onScheduleNotesChange?: (val: string) => void;
  submitted?: boolean;
}

const tone = (csc: number, gc: number): string => {
  if (gc === 0 || csc === 0) return "amber";
  const ratio = gc / csc;
  if (ratio <= 1.05) return "green";
  if (ratio <= 1.2) return "amber";
  return "red";
};

const Step3ProgressPhases = ({
  prefill,
  onPhaseNotesChange,
  scheduleNotes = "",
  onScheduleNotesChange,
  submitted = false,
}: Props) => {
  const snapshot = prefill?.inspectionSnapshot;
  const inspection = prefill?.inspection;
  const phases = snapshot?.phases ?? [];
  const snapshotLineItems = snapshot?.lineItems ?? [];
  const documents = prefill?.documents ?? [];
  const hasPhases = phases.length > 0;
  const isCommercial = prefill?.project?.type === "commercial";
  const hasLineItems = !hasPhases && snapshotLineItems.length > 0;
  const rootRef = useRef<HTMLDivElement>(null);

  const inspectorLine = inspection
    ? `${inspection.inspectorName}, ${inspection.inspectionDate}`
    : "";
  const overallCsc =
    snapshot?.overallCscEstimation ??
    (inspection?.cscEstimation
      ? parseFloat(inspection.cscEstimation)
      : undefined);
  const flagCount = hasPhases
    ? phases.reduce(
        (acc, phase) => acc + phase.items.filter((i) => i.isFlagged).length,
        0,
      )
    : snapshotLineItems.filter((i) => i.isFlagged).length;

  const [notes, setNotes] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const seedNotes = (
      items: Array<{ id: string; note?: Record<string, string> }>,
    ) => {
      items.forEach((item) => {
        const noteObj = item.note ?? {};
        const lines = Object.entries(noteObj)
          .filter(([k]) => k !== "flag")
          .map(([, v]) => v)
          .filter(Boolean)
          .map((n) => `• ${n}`)
          .join("\n");
        if (lines) map[item.id] = lines;
      });
    };
    seedNotes((snapshot?.phases ?? []).flatMap((p) => p.items));
    seedNotes(snapshot?.lineItems ?? []);
    return map;
  });

  useEffect(() => {
    onPhaseNotesChange?.(notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  const scheduleErr =
    submitted && !scheduleNotes.replace(/^•\s*/gm, "").trim()
      ? "Schedule Notes is required"
      : "";

  useEffect(() => {
    if (submitted && scheduleErr) {
      rootRef.current
        ?.querySelector<HTMLElement>(".field-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const handleScheduleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next =
      scheduleNotes.substring(0, start) + "\n• " + scheduleNotes.substring(end);
    onScheduleNotesChange?.(next);
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + 3;
    }, 0);
  };

  const handleScheduleFocus = () => {
    if (!scheduleNotes) onScheduleNotesChange?.("• ");
  };

  const handleNoteFocus = (id: string) => {
    if (!notes[id]) {
      setNotes((prev) => ({ ...prev, [id]: "• " }));
    }
  };

  const handleNoteKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    id: string,
  ) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const val = notes[id] ?? "";
    const next = val.substring(0, start) + "\n• " + val.substring(end);
    setNotes((prev) => ({ ...prev, [id]: next }));
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + 3;
    }, 0);
  };

  return (
    <div className="craScreen" ref={rootRef}>
      <SectionHeader
        title="Construction Progress Report"
        subtitle="Phase percentages and notes are pre-populated from FI submission."
      />
      {inspectorLine && (
        <SourceBanner
          icon="📋"
          title={`From FI Submission — ${inspectorLine}`}
          text={`Overall CSC estimate ${overallCsc !== undefined ? `${overallCsc}%` : "—"}. ${flagCount} flags raised. ${documents.length == 1 ? "1 photo" : `${documents.length} photos`} captured.`}
        />
      )}
      <ResidentialEstimateNote isCommercial={isCommercial} />
      {hasPhases &&
        phases.map((phase) => {
          const cscPct = phase.phaseCscEstimation;
          const fillW = Math.min(Math.round(cscPct), 100);
          const t = tone(cscPct, cscPct);

          return (
            <div key={phase.phase} className="phaseBlock">
              <div className="phaseBlock__header">
                <h4>
                  Phase {phase.phase} — {phase.phaseWeight}% weight
                </h4>
                <div className="phaseBlock__pcts">
                  <div className={`cscStat ${t}`}>
                    <span className="cscStat__val">~{Math.round(cscPct)}%</span>
                    <span className="cscStat__label">CSC EST.</span>
                  </div>
                </div>
              </div>
              <div className="phaseBlock__track">
                <div
                  className={`phaseBlock__fill ${t}`}
                  style={{ width: `${fillW}%` }}
                />
              </div>

              {sortByItemCode(phase.items).map((item) => (
                <div key={item.id} className="phaseItemRow">
                  <div className="phaseItemLabel">
                    <span className="itemCode">{item.itemCode}</span>
                    {item.itemLabel}
                  </div>
                  <div className="craField">
                    <label>Phase Narrative Note</label>
                    <TextArea
                      rows={3}
                      value={notes[item.id] ?? ""}
                      onChange={(e) =>
                        setNotes((prev) => ({
                          ...prev,
                          [item.id]: e.target.value,
                        }))
                      }
                      onFocus={() => handleNoteFocus(item.id)}
                      onKeyDown={(e) => handleNoteKeyDown(e, item.id)}
                      maxChars={CHAR_LIMIT.DESCRIPTION}
                    />
                  </div>
                </div>
              ))}
            </div>
          );
        })}

      {hasLineItems &&
        snapshotLineItems.map((li) => {
          const cscPct = parseFloat(li.cscEstimation) || 0;
          const gcPct = li.gcBilledPct ?? 0;
          const t = tone(cscPct, gcPct);
          const fillW = Math.min(Math.round(cscPct), 100);

          return (
            <div key={li.id} className="phaseBlock">
              <div className="phaseBlock__header">
                <h4>{li.aiaItem.itemName} </h4>
                <div className="phaseBlock__pcts">
                  <div className={`cscStat ${t}`}>
                    <span className="cscStat__val">~{Math.round(cscPct)}%</span>
                    <span className="cscStat__label">CSC EST.</span>
                  </div>
                  {isCommercial && (
                    <div className={`cscStat ${t}`}>
                      <span className="cscStat__val">
                        ~{Math.round(gcPct)}%
                      </span>
                      <span className="cscStat__label">GC Billed</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="phaseBlock__track">
                <div
                  className={`phaseBlock__fill ${t}`}
                  style={{ width: `${fillW}%` }}
                />
              </div>
              <div className="craField">
                <label>Phase Narrative Notes</label>
                <TextArea
                  rows={3}
                  value={notes[li.id] ?? ""}
                  onChange={(e) =>
                    setNotes((prev) => ({ ...prev, [li.id]: e.target.value }))
                  }
                  onFocus={() => handleNoteFocus(li.id)}
                  onKeyDown={(e) => handleNoteKeyDown(e, li.id)}
                  maxChars={CHAR_LIMIT.DESCRIPTION}
                />
              </div>
            </div>
          );
        })}

      <Card
        title="Schedule Update"
        badge={{ label: "Editable", variant: "editable" }}
      >
        <div className="craField">
          <label>
            Schedule Notes <span className="req">*</span>
          </label>
          <TextArea
            rows={4}
            value={scheduleNotes}
            onChange={(e) => onScheduleNotesChange?.(e.target.value)}
            onFocus={handleScheduleFocus}
            onKeyDown={handleScheduleKeyDown}
            className={scheduleErr ? "error-input-red" : ""}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          {scheduleErr && <p className="field-error">{scheduleErr}</p>}
        </div>
      </Card>
    </div>
  );
};

export default Step3ProgressPhases;
